import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Zap, TrendingUp, Shield, Users, Target, DollarSign, Rocket, BookOpen, Clock } from 'lucide-react';

export const BenefitsTabs = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  const tabsData = {
    seo: {
      title: 'SEO & Content Teams',
      benefits: [
        {
          icon: Zap,
          title: 'Faster Indexing',
          description:
            'Backlinks from high-authority sites signal Google to crawl your content faster. Stop waiting weeks for your articles to get indexed.',
        },
        {
          icon: TrendingUp,
          title: 'Systematic DA/PA Growth',
          description:
            'Consistently placing DoFollow links on high-DA sites is the proven path to growing domain authority. Searchefy makes this systematic, not random.',
        },
        {
          icon: Shield,
          title: 'Full Coverage Tracking',
          description:
            'Know which pages have zero backlinks and prioritize outreach for your highest-value content.',
        },
      ],
    },
    marketing: {
      title: 'Marketing Heads',
      benefits: [
        {
          icon: Users,
          title: 'Brand Visibility at Scale',
          description:
            'Every published backlink is a brand mention on a trusted third-party site. More quality references = more perceived authority in your niche.',
        },
        {
          icon: Target,
          title: 'Campaign Accountability',
          description:
            'Replace vague "doing SEO" reports with clear success rate data per campaign — fully reportable to any stakeholder.',
        },
        {
          icon: DollarSign,
          title: 'Zero Recurring Cost',
          description:
            'One $9 payment. No monthly SaaS fees. More budget for content and distribution.',
        },
      ],
    },
    founders: {
      title: 'Founders',
      benefits: [
        {
          icon: Rocket,
          title: 'Level the Playing Field',
          description:
            'Established players have years of backlinks. Searchefy gives you a structured system to build authority from ground zero.',
        },
        {
          icon: BookOpen,
          title: 'No Agency Required',
          description:
            'The curated directory makes link-building accessible to any founder — no SEO expertise needed.',
        },
        {
          icon: Clock,
          title: 'Get Content Indexed Faster',
          description:
            'New sites struggle with Google discovery. Backlinks from established sites fast-track your pages into search results.',
        },
      ],
    },
  };

  return (
    <section
      id="benefits"
      data-testid="benefits-section"
      className="section-light py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-4">
            What Searchefy Does for{' '}
            <span className="gradient-text">Your Business</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Beyond features — here's the real-world impact on your SEO.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Tabs defaultValue="seo" className="w-full">
            <TabsList className="grid w-full max-w-xl mx-auto grid-cols-3 mb-12 bg-gray-100 p-1 rounded-full h-auto">
              <TabsTrigger
                value="seo"
                data-testid="tab-seo"
                className="rounded-full py-3 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                SEO Teams
              </TabsTrigger>
              <TabsTrigger
                value="marketing"
                data-testid="tab-marketing"
                className="rounded-full py-3 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                Marketing Heads
              </TabsTrigger>
              <TabsTrigger
                value="founders"
                data-testid="tab-founders"
                className="rounded-full py-3 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                Founders
              </TabsTrigger>
            </TabsList>

            {Object.entries(tabsData).map(([key, data]) => (
              <TabsContent key={key} value={key} data-testid={`tab-content-${key}`}>
                <div className="grid md:grid-cols-3 gap-8">
                  {data.benefits.map((benefit, index) => (
                    <motion.div
                      key={benefit.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="p-6 rounded-2xl bg-white border border-gray-200 hover:shadow-lg transition-shadow"
                    >
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 mb-4">
                        <benefit.icon size={24} className="text-blue-600" />
                      </div>
                      <h3 className="font-display text-lg font-bold text-gray-900 mb-2">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {benefit.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </motion.div>
      </div>
    </section>
  );
};

export default BenefitsTabs;
